import dayjs from 'dayjs'
import { fetchActivities, fetchContactNotes, fetchWorkflows, deduplicateById } from '../shared/redtailQueryHelpers'

function filterByCategoryIds (activities, config) {
  const categoryIds = config?.categoryIds
  if (!categoryIds?.length) return activities
  return activities.filter(a => categoryIds.includes(a.category_id))
}

const upcomingMeetings = {
  key: 'upcoming-meetings',
  displayName: 'Upcoming Meetings',
  defaults: {
    title: 'Upcoming Meetings',
    limit: 5,
    lookaheadDays: 90,
    icon: 'redtailSmall'
  },

  async fetchData (members, clientId, config) {
    const lookaheadDays = config?.lookaheadDays ?? 90
    return fetchActivities(members, clientId, {
      start_date: dayjs().format('YYYY-MM-DD'),
      end_date: dayjs().add(lookaheadDays, 'day').format('YYYY-MM-DD')
    })
  },

  compute (activities, config) {
    const limit = config?.limit ?? 5
    const priorityFilter = config?.priorityFilter
    let filtered = filterByCategoryIds(activities, config)
      .filter(a => !a.completed)
    if (priorityFilter?.length) {
      const coerced = priorityFilter.map(String)
      filtered = filtered.filter(a => coerced.includes(String(a.priority)))
    }

    return filtered
      .sort((a, b) => {
        const da = a.start_date ? dayjs(a.start_date).valueOf() : Infinity
        const db = b.start_date ? dayjs(b.start_date).valueOf() : Infinity
        return da - db
      })
      .slice(0, limit)
  }
}

const latestNotes = {
  key: 'latest-notes',
  displayName: 'Latest Notes',
  defaults: {
    title: 'Notes',
    pinnedLimit: 10,
    recentLimit: 6,
    notePreviewLength: 200,
    icon: 'redtailSmall'
  },

  async fetchData (members, clientId) {
    return fetchContactNotes(members, clientId)
  },

  compute (allNotes, config) {
    const pinnedLimit = config?.pinnedLimit ?? 10
    const recentLimit = config?.recentLimit ?? 6

    const sortDesc = (a, b) => {
      const da = a.created_at ? dayjs(a.created_at).valueOf() : 0
      const db = b.created_at ? dayjs(b.created_at).valueOf() : 0
      return db - da
    }

    const nonDraft = allNotes.filter(n => !n.draft && !n.deleted)

    const pinned = nonDraft
      .filter(n => n.pinned === true)
      .sort(sortDesc)
      .slice(0, pinnedLimit)

    const recent = nonDraft
      .filter(n => n.pinned !== true)
      .sort(sortDesc)
      .slice(0, recentLimit)

    return { pinned, recent }
  }
}

const workflows = {
  key: 'workflows',
  displayName: 'Workflows',
  defaults: {
    title: 'Workflows',
    limit: 10,
    icon: 'redtailSmall'
  },

  async fetchData (members, clientId) {
    const results = await fetchWorkflows(members, clientId)
    return deduplicateById(results)
  },

  compute (allWorkflows, config) {
    const limit = config?.limit ?? 10

    return [...allWorkflows]
      .sort((a, b) => {
        const da = a.due_date ? dayjs(a.due_date).valueOf() : 0
        const db = b.due_date ? dayjs(b.due_date).valueOf() : 0
        return db - da
      })
      .slice(0, limit)
  }
}

const upcomingTasks = {
  key: 'upcoming-tasks',
  displayName: 'Upcoming Tasks',
  defaults: {
    title: 'Client Tasks',
    limit: 10,
    lookaheadDays: 365,
    lookbackDays: 365,
    icon: 'redtailSmall'
  },

  async fetchData (members, clientId, config) {
    const lookbackDays = config?.lookbackDays ?? 365
    const lookaheadDays = config?.lookaheadDays ?? 365
    return fetchActivities(members, clientId, {
      start_date: dayjs().subtract(lookbackDays, 'day').format('YYYY-MM-DD'),
      end_date: dayjs().add(lookaheadDays, 'day').format('YYYY-MM-DD')
    })
  },

  compute (activities, config) {
    const limit = config?.limit ?? 10
    const activityCodeIds = config?.activityCodeIds ?? [1]
    const completedFilter = config?.completedFilter ?? 'open'
    const priorityFilter = config?.priorityFilter

    let filtered = deduplicateById(activities)

    filtered = filtered.filter(a => activityCodeIds.includes(a.activity_code_id))

    if (completedFilter === 'open') {
      filtered = filtered.filter(a => !a.completed)
    } else if (completedFilter === 'completed') {
      filtered = filtered.filter(a => a.completed)
    }

    filtered = filterByCategoryIds(filtered, config)

    if (priorityFilter?.length) {
      const coerced = priorityFilter.map(String)
      filtered = filtered.filter(a => coerced.includes(String(a.priority)))
    }

    return filtered
      .sort((a, b) => {
        const da = a.end_date ? dayjs(a.end_date).valueOf() : Infinity
        const db = b.end_date ? dayjs(b.end_date).valueOf() : Infinity
        return da - db
      })
      .slice(0, limit)
  }
}

const presets = {
  'upcoming-meetings': upcomingMeetings,
  'latest-notes': latestNotes,
  workflows: workflows,
  'upcoming-tasks': upcomingTasks
}

export default presets
